/**
 * AdminApiClient — Cliente HTTP del panel de administración (ESM puro)
 *
 * Módulo independiente sin referencias al shell App.
 * Requiere inicialización explícita via init() antes de usarse.
 *
 * Diseño: inyección de dependencias lazy para romper la circularidad
 * App ↔ Api que existía cuando vivía en admin.js.
 */

// ============================================================
// CONFIGURACIÓN
// ============================================================
import { key as storageKey } from '@shared/config/storage-namespace.js';
import { conMonedaDeVista } from './moneda-vista-en-requests.js';

// VITE_API_URL_PUBLIC es la base del client API (callPublic). Obligatoria en build.
// Generada por scripts/generate-env.js desde infra/brands/<id>.json.
const VITE_API_URL_PUBLIC = import.meta.env.VITE_API_URL_PUBLIC;
if (!VITE_API_URL_PUBLIC) {
    throw new Error('[AdminApiClient] VITE_API_URL_PUBLIC obligatorio. Verificar .env del build.');
}
const PUBLIC_API_URL = `${VITE_API_URL_PUBLIC}/api`;
const ADMIN_API_URL = '/api';
// SSOT: debe coincidir con ADMIN_TOKEN_KEY en admin.js (ambos usan storageKey('admin_token')).
const ADMIN_TOKEN_KEY = storageKey('admin_token');
// Mismo criterio que App.FETCH_TIMEOUT_MS: ningún fetch queda colgado sin límite.
const REQUEST_TIMEOUT_MS = 60000;

// Dependencias inyectadas por App.showApp() via init()
let _getTokenFn = () => null;
let _onErrorFn = msg => console.error('[AdminApiClient]', msg);

// ============================================================
// API PÚBLICA DEL MÓDULO
// ============================================================

export const AdminApiClient = {
    /**
     * Inyecta las dependencias del shell.
     * Llamar una vez desde App después de que App esté definido.
     */
    init({ getToken, onError }) {
        _getTokenFn = getToken;
        _onErrorFn = onError;
    },

    async callPublic(endpoint) {
        try {
            const res = await fetch(`${PUBLIC_API_URL}${endpoint}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return await res.json();
        } catch (e) {
            console.error('[API Public Error]', e);
            _onErrorFn('Error de conexion (API)');
            return null;
        }
    },

    /**
     * Variante de callAdmin que NO lanza error en HTTP 409 con `codigo` específico
     * (ej. BEARER_MULTI_SORTEO_PENDIENTES). En su lugar devuelve el body parseado
     * con `{ok:false, codigo, ...}` para que el caller pueda decidir el flujo
     * (mostrar dialog de confirmación, etc).
     *
     * Para todos los demás estados delega al callAdmin estándar (lanza el error).
     * Usar SOLO en flujos donde un 409 con código de negocio es resultado
     * esperado (stop/delete de sorteo con bearer pendientes).
     */
    async callAdminConfirmable(endpoint, method = 'POST', body = null) {
        try {
            const token = _getTokenFn();
            const opts = {
                method,
                headers: { 'X-Admin-Token': token },
            };
            if (body) {
                opts.headers['Content-Type'] = 'application/json';
                opts.body = JSON.stringify(body);
            }
            const res = await fetch(`${ADMIN_API_URL}${endpoint}`, opts);
            if (res.status === 401) {
                if (typeof window !== 'undefined') {
                    localStorage.removeItem(ADMIN_TOKEN_KEY);
                    window.location.reload();
                }
                throw new Error('Sesión expirada');
            }
            // 409 con `codigo` se devuelve al caller — es un resultado de negocio,
            // no un error de transporte. El caller decide qué hacer.
            if (res.status === 409) {
                const body409 = await res.json().catch(() => null);
                if (body409 && body409.codigo) return body409;
                // 409 sin código de negocio → tratar como error normal.
                throw new Error(body409?.error || 'Conflict (409)');
            }
            if (!res.ok) {
                let serverError = `HTTP ${res.status}`;
                try {
                    const errBody = await res.json();
                    if (errBody.error) serverError = errBody.error;
                } catch (_) {}
                throw new Error(serverError);
            }
            return await res.json();
        } catch (e) {
            console.error('[API Admin Confirmable Error]', e);
            _onErrorFn(e.message || 'Error de conexion (Admin)');
            return null;
        }
    },

    async callAdmin(endpoint, method = 'POST', body = null) {
        // Sin timeout, un fetch que nunca settlea dejaría clavado el guard in-flight
        // del polling del dashboard y el polling moriría en silencio.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
            const token = _getTokenFn();
            const opts = {
                method,
                headers: { 'X-Admin-Token': token },
                signal: controller.signal,
            };
            if (body) {
                opts.headers['Content-Type'] = 'application/json';
                opts.body = JSON.stringify(body);
            }
            // Selector de moneda del panel: mismo SSOT que usa App.fetchWithAuth.
            // Esta capa arma sus URLs aparte (base y prefijo distintos), así que sin
            // esto sus lecturas —command-center entre ellas— quedaban fuera del selector.
            const res = await fetch(`${ADMIN_API_URL}${conMonedaDeVista(endpoint, method)}`, opts);
            if (res.status === 401) {
                // Sesión expirada — limpiar token y recargar (el reload va al login)
                if (typeof window !== 'undefined') {
                    localStorage.removeItem(ADMIN_TOKEN_KEY);
                    window.location.reload();
                }
                throw new Error('Sesión expirada');
            }
            if (!res.ok) {
                let serverError = `HTTP ${res.status}`;
                let detalles = null;
                try {
                    const errBody = await res.json();
                    if (errBody.error) serverError = errBody.error;
                    if (Array.isArray(errBody.detalles) && errBody.detalles.length > 0) {
                        detalles = errBody.detalles;
                        // Compactar primeros 3 motivos para que el toast sea útil
                        // (campo + motivo). Defense in depth: el shape lo define
                        // validar-payload-contra-contrato.js (path/motivo).
                        const resumen = detalles
                            .slice(0, 3)
                            .map(d => `${d.path}: ${d.motivo}`)
                            .join('; ');
                        serverError = `${serverError} — ${resumen}`;
                    }
                } catch (_) {}
                const err = new Error(serverError);
                if (detalles) err.detalles = detalles;
                throw err;
            }
            return await res.json();
        } catch (e) {
            // AbortError = nuestro timeout — mensaje claro en vez del críptico del browser.
            const msg = e.name === 'AbortError' ? 'El servidor tardó demasiado en responder' : e.message;
            console.error('[API Admin Error]', e, e.detalles || '');
            _onErrorFn(msg || 'Error de conexion (Admin)');
            return null;
        } finally {
            clearTimeout(timeoutId);
        }
    },

    /**
     * Upload multipart (archivos) — NO usa Content-Type JSON.
     * El browser setea el boundary automáticamente con FormData.
     */
    async uploadFile(endpoint, formData) {
        try {
            const token = _getTokenFn();
            const res = await fetch(`${ADMIN_API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'X-Admin-Token': token },
                body: formData,
            });
            if (!res.ok) {
                let serverError = `HTTP ${res.status}`;
                try {
                    const errBody = await res.json();
                    if (errBody.error) serverError = errBody.error;
                } catch (_) {}
                throw new Error(serverError);
            }
            return await res.json();
        } catch (e) {
            console.error('[API Upload Error]', e);
            _onErrorFn(e.message || 'Error subiendo archivo');
            return null;
        }
    },

    // === LECTURAS (API pública) ===
    getSorteoConfigs: () => AdminApiClient.callAdmin('/admin/lotowheel/configs', 'GET'),
    // hidratar=true: el backend aplica defaults del contrato canónico al
    // snapshot crudo antes de devolverlo. Pensado para el modal admin: arranca
    // con un snapshot SANO, sin nulls heredados ni basura histórica.
    getSorteoConfig: id => AdminApiClient.callPublic(`/lotowheel/configs/${id}?hidratar=true`),
    getDispositivos: () => AdminApiClient.callAdmin('/admin/dispositivos', 'GET'),
    getDispositivoById: id => AdminApiClient.callAdmin(`/admin/dispositivos/${id}`, 'GET'),
    getPuntoVentaGameTypes: puntoVentaId => AdminApiClient.callPublic(`/puntos-venta/${puntoVentaId}/game-types`),
    getPuntoVentaSorteos: (puntoVentaId, gameTypeId) =>
        AdminApiClient.callPublic(`/puntos-venta/${puntoVentaId}/sorteos/${gameTypeId}`),

    // === ADMIN LECTURAS (solo admin) ===
    // Antes apuntaba a '/game-types' (el endpoint PÚBLICO de health.js, sin
    // auth) por error de copiado — el admin mandaba el token igual pero nunca
    // se validaba. '/admin/game-types' (admin-game-engine.js, con
    // adminAuthPreHandler) era código muerto sin consumidores. Corregido para
    // que el admin use su propio endpoint autenticado, coherente con el resto
    // de este archivo (getDispositivos, getSorteoConfigs, etc.).
    getGameTypes: () => AdminApiClient.callAdmin('/admin/game-types', 'GET'),
    getDashboard: () => AdminApiClient.callAdmin('/admin/dashboard', 'GET'),
    getCommandCenter: (range = '20', configId = 'all', desde = null, hasta = null) => {
        let url = `/admin/command-center?range=${range}&configId=${configId}`;
        if (desde) url += `&desde=${desde}`;
        if (hasta) url += `&hasta=${hasta}`;
        return AdminApiClient.callAdmin(url, 'GET');
    },
    getSorteo: id => AdminApiClient.callAdmin(`/admin/sorteos/${id}`, 'GET'),
    getBloqueos: () => AdminApiClient.callAdmin('/admin/bloqueos', 'GET'),

    // === ESCRITURAS (Admin) ===
    createBloqueo: data => AdminApiClient.callAdmin('/admin/bloqueos', 'POST', data),
    deleteBloqueo: id => AdminApiClient.callAdmin(`/admin/bloqueos/${id}`, 'DELETE'),
    createLotowheelConfig: data => AdminApiClient.callAdmin('/lotowheel/configs', 'POST', data),
    updateLotowheelConfig: (id, data) => AdminApiClient.callAdmin(`/lotowheel/configs/${id}`, 'PUT', data),
    updateDispositivo: (id, data) => AdminApiClient.callAdmin(`/dispositivos/${id}`, 'PUT', data),
    deleteDispositivo: id => AdminApiClient.callAdmin(`/dispositivos/${id}/delete`, 'POST'),

    // === SORTEOS VIRTUALES ===
    getVirtualDraws: () => AdminApiClient.callAdmin('/admin/virtual-draws', 'GET'),
    getVirtualDrawsStats: () => AdminApiClient.callAdmin('/admin/virtual-draws/stats', 'GET'),
    createVirtualDraw: data => AdminApiClient.callAdmin('/admin/virtual-draws/create', 'POST', data),
    stopVirtualDraw: drawId =>
        AdminApiClient.callAdmin(`/admin/virtual-draws/${encodeURIComponent(drawId)}/stop`, 'POST'),
    triggerVirtualDraw: drawId =>
        AdminApiClient.callAdmin(`/admin/virtual-draws/${encodeURIComponent(drawId)}/trigger`, 'POST'),
    recoverVirtualDraws: () => AdminApiClient.callAdmin('/admin/virtual-draws/recover', 'POST'),
};
